/**
 * InfoTip — a warning badge that explains itself.
 *
 * A bare "⚠" next to a token tells the user something is wrong without saying
 * what, which is worse than no badge at all. Tapping shows the explanation as a
 * small bubble that fades away on its own; hovering shows it too on desktop.
 */

import { useEffect, useRef, useState } from 'react'

const VISIBLE_MS = 3500

export default function InfoTip({
  text,
  label = '⚠',
  color = '#f0a35e',
}: {
  text: string
  label?: string
  color?: string
}) {
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const show = () => {
    setOpen(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setOpen(false), VISIBLE_MS)
  }

  const visible = open || hovered

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        type="button"
        aria-label={text}
        title={text}
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
          show()
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: 'none',
          border: 'none',
          padding: '0 2px',
          fontSize: 10,
          lineHeight: 1,
          color,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>

      <span
        role="tooltip"
        style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
          width: 220,
          borderRadius: 10,
          background: '#1c231c',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 22px rgba(0,0,0,0.5)',
          padding: '8px 10px',
          fontSize: 11,
          lineHeight: 1.35,
          color: '#d7e0d8',
          textAlign: 'left',
          pointerEvents: 'none',
          opacity: visible ? 1 : 0,
          transition: 'opacity 220ms ease',
        }}
      >
        {text}
      </span>
    </span>
  )
}
